/**
 * emailParser — browser-safe RFC 822 / MIME reader for `.eml` uploads.
 *
 * Before this module, `.eml` uploads were stored as their raw source: MIME
 * headers, quoted-printable HTML and the base64 bytes of every inline
 * signature image. The 10,000-character extraction budget was spent on
 * noise and the message body was effectively lost (Pineywoods Park utility
 * emails, 2026-09-07). This parser turns the source into what a reader
 * would want cited:
 *
 *   From: …            ← only the five identity headers, one line each
 *   To: …
 *   Cc: …
 *   Date: …
 *   Subject: …
 *
 *   <message body>     ← text/plain preferred, HTML stripped as a fallback
 *
 *   Attachments: a.pdf; b.png   ← trailer, only when attachments exist
 *
 * Design constraints:
 *   - No dependencies. Runs in the browser upload path and under vitest.
 *   - Multipart is walked recursively (mixed → alternative → related).
 *   - `Content-Transfer-Encoding` quoted-printable and base64 are decoded to
 *     bytes and re-read with the part's declared charset.
 *   - Image parts and attachments never contribute text.
 *   - Output is deterministic so `looksLikeParsedEmail` can recognise it.
 */

export interface EmailAttachment {
  fileName: string;
  mimeType: string;
  bytes: Uint8Array;
}

export interface ParsedEmail {
  /** Identity headers, decoded (RFC 2047) and unfolded. */
  headers: {
    from?: string;
    to?: string;
    cc?: string;
    date?: string;
    subject?: string;
  };
  /** Readable body: text/plain when present, otherwise HTML stripped to text. */
  body: string;
  /** Filenames of parts flagged as attachments (inline images are skipped). */
  attachments: string[];
  /** Decoded visible attachments; never included in the message's text. */
  attachmentsData?: EmailAttachment[];
  /** True when the input carried at least the headers an email must have. */
  isEmail: boolean;
  /** Canonical text for `SiteDocument.textContent` (see module docblock). */
  text: string;
}

/** Body placeholder emitted when no readable part exists. */
export const NO_READABLE_BODY = '[No readable message body]';

const IDENTITY_HEADERS = ['from', 'to', 'cc', 'date', 'subject'] as const;
type IdentityHeader = (typeof IDENTITY_HEADERS)[number];
const IDENTITY_LABEL: Record<IdentityHeader, string> = {
  from: 'From',
  to: 'To',
  cc: 'Cc',
  date: 'Date',
  subject: 'Subject',
};

/* ── Byte helpers ─────────────────────────────────────────────────── */

function base64ToBytes(input: string): Uint8Array {
  const clean = input.replace(/[^A-Za-z0-9+/=]/g, '');
  if (clean.length === 0) return new Uint8Array(0);
  let binary: string;
  try {
    binary = typeof atob === 'function'
      ? atob(clean)
      : Buffer.from(clean, 'base64').toString('binary');
  } catch {
    return new Uint8Array(0);
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i) & 0xff;
  return bytes;
}

function quotedPrintableToBytes(input: string): Uint8Array {
  // Soft line breaks (`=` at end of line) join the next line.
  const src = input.replace(/=\r?\n/g, '');
  const out: number[] = [];
  const encoder = new TextEncoder();
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (ch === '=') {
      const hex = src.slice(i + 1, i + 3);
      if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
        out.push(parseInt(hex, 16));
        i += 2;
        continue;
      }
    }
    const code = ch.charCodeAt(0);
    if (code < 0x80) {
      out.push(code);
    } else {
      // 8-bit literal inside a QP body (technically invalid, seen in the
      // wild): re-encode the already-decoded character as UTF-8 bytes.
      for (const b of encoder.encode(ch)) out.push(b);
    }
  }
  return Uint8Array.from(out);
}

function bytesToText(bytes: Uint8Array, charset: string | undefined): string {
  const label = (charset ?? 'utf-8').trim().toLowerCase().replace(/^"|"$/g, '') || 'utf-8';
  try {
    return new TextDecoder(label, { fatal: false }).decode(bytes);
  } catch {
    return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  }
}

/* ── Header helpers ───────────────────────────────────────────────── */

/** RFC 2047 encoded-word decoding: `=?utf-8?B?...?=` / `=?iso-8859-1?Q?...?=`. */
export function decodeEncodedWords(value: string): string {
  // Whitespace between two encoded words is not significant (RFC 2047 §6.2).
  const joined = value.replace(/\?=\s+=\?/g, '?==?');
  return joined.replace(
    /=\?([^?]+)\?([bBqQ])\?([^?]*)\?=/g,
    (_m, charset: string, enc: string, payload: string) => {
      const bytes = enc.toLowerCase() === 'b'
        ? base64ToBytes(payload)
        : quotedPrintableToBytes(payload.replace(/_/g, ' '));
      return bytesToText(bytes, charset);
    },
  );
}

interface HeaderMap {
  get(name: string): string | undefined;
  has(name: string): boolean;
}

function parseHeaders(headerText: string): HeaderMap {
  const map = new Map<string, string>();
  const lines = headerText.split(/\r?\n/);
  let current: { name: string; value: string } | null = null;
  const commit = () => {
    if (!current) return;
    const key = current.name.toLowerCase();
    const existing = map.get(key);
    map.set(key, existing ? `${existing}, ${current.value.trim()}` : current.value.trim());
    current = null;
  };
  for (const line of lines) {
    if (line.length === 0) continue;
    if (/^[ \t]/.test(line) && current) {
      // Folded continuation line.
      current.value += ` ${line.trim()}`;
      continue;
    }
    const idx = line.indexOf(':');
    if (idx <= 0) continue; // not a header line; ignore defensively
    commit();
    current = { name: line.slice(0, idx).trim(), value: line.slice(idx + 1) };
  }
  commit();
  return {
    get: (name) => map.get(name.toLowerCase()),
    has: (name) => map.has(name.toLowerCase()),
  };
}

interface ParamHeader {
  value: string;
  params: Record<string, string>;
}

/** Parse `type/subtype; a=b; c="d e"` style headers (Content-Type, Content-Disposition). */
function parseParamHeader(raw: string | undefined): ParamHeader {
  if (!raw) return { value: '', params: {} };
  const semi = raw.indexOf(';');
  const value = (semi >= 0 ? raw.slice(0, semi) : raw).trim().toLowerCase();
  const params: Record<string, string> = {};
  if (semi < 0) return { value, params };
  const rest = raw.slice(semi + 1);
  const re = /\s*([\w*.-]+)\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^;]*))\s*(?:;|$)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(rest)) !== null) {
    if (m[0].length === 0) { re.lastIndex++; continue; }
    const key = m[1]!.toLowerCase();
    let val = (m[2] ?? m[3] ?? '').trim();
    if (key.endsWith('*')) {
      // RFC 2231: `filename*=utf-8''My%20file.pdf`
      const base = key.slice(0, -1);
      const enc = val.match(/^([^']*)'[^']*'(.*)$/);
      if (enc) {
        try { val = decodeURIComponent(enc[2]!); } catch { val = enc[2]!; }
      }
      if (!params[base]) params[base] = val;
      continue;
    }
    if (!(key in params)) params[key] = val;
  }
  return { value, params };
}

/* ── MIME tree ────────────────────────────────────────────────────── */

interface RawPart {
  headers: HeaderMap;
  body: string;
}

function splitHeadersAndBody(raw: string): RawPart {
  const m = /\r?\n\r?\n/.exec(raw);
  if (!m) {
    // No blank line: either headers only or a bare body. Treat a block that
    // starts with a header line as headers-only, else as body.
    if (/^[\w-]+:/.test(raw)) return { headers: parseHeaders(raw), body: '' };
    return { headers: parseHeaders(''), body: raw };
  }
  return {
    headers: parseHeaders(raw.slice(0, m.index)),
    body: raw.slice(m.index + m[0].length),
  };
}

function splitMultipart(body: string, boundary: string): string[] {
  const delimiter = `--${boundary}`;
  const closing = `${delimiter}--`;
  const lines = body.split(/\r?\n/);
  const parts: string[] = [];
  let current: string[] | null = null;
  for (const line of lines) {
    const trimmed = line.replace(/\s+$/, '');
    if (trimmed === closing) {
      if (current) parts.push(current.join('\n'));
      current = null;
      break;
    }
    if (trimmed === delimiter) {
      if (current) parts.push(current.join('\n'));
      current = [];
      continue;
    }
    if (current) current.push(line);
  }
  if (current) parts.push(current.join('\n'));
  return parts;
}

interface Collected {
  texts: Array<{ kind: 'plain' | 'html'; text: string }>;
  attachments: string[];
  attachmentsData: EmailAttachment[];
}

function decodePartBody(part: RawPart, charset: string | undefined): string {
  const cte = (part.headers.get('content-transfer-encoding') ?? '7bit').trim().toLowerCase();
  if (cte === 'base64') return bytesToText(base64ToBytes(part.body), charset);
  if (cte === 'quoted-printable') return bytesToText(quotedPrintableToBytes(part.body), charset);
  return part.body;
}

function collect(part: RawPart, depth: number): Collected {
  const out: Collected = { texts: [], attachments: [], attachmentsData: [] };
  if (depth > 12) return out;
  const contentType = parseParamHeader(part.headers.get('content-type') ?? 'text/plain');
  const disposition = parseParamHeader(part.headers.get('content-disposition'));
  const type = contentType.value || 'text/plain';
  const filename = disposition.params['filename'] ?? contentType.params['name'];

  if (type.startsWith('multipart/')) {
    const boundary = contentType.params['boundary'];
    const children = boundary ? splitMultipart(part.body, boundary) : [];
    if (children.length === 0) {
      // Malformed multipart: fall back to reading the body as text.
      const text = part.body.trim();
      if (text) out.texts.push({ kind: 'plain', text });
      return out;
    }
    const collected = children.map((child) => collect(splitHeadersAndBody(child), depth + 1));
    if (type === 'multipart/alternative') {
      // One representation only: prefer a non-empty text/plain, else HTML.
      const plain = collected.find((c) => c.texts.some((t) => t.kind === 'plain' && t.text.trim().length > 0));
      const html = collected.find((c) => c.texts.some((t) => t.kind === 'html' && t.text.trim().length > 0));
      const chosen = plain ?? html ?? collected.find((c) => c.texts.length > 0);
      if (chosen) out.texts.push(...chosen.texts);
      for (const c of collected) {
        out.attachments.push(...c.attachments);
        out.attachmentsData.push(...c.attachmentsData);
      }
      return out;
    }
    for (const c of collected) {
      out.texts.push(...c.texts);
      out.attachments.push(...c.attachments);
      out.attachmentsData.push(...c.attachmentsData);
    }
    return out;
  }

  const isAttachment = disposition.value === 'attachment'
    || (filename !== undefined && !type.startsWith('text/') && !type.startsWith('image/'));
  if (isAttachment) {
    const fileName = decodeEncodedWords(filename ?? `(unnamed ${type})`);
    out.attachments.push(fileName);
    const cte = (part.headers.get('content-transfer-encoding') ?? '').trim().toLowerCase();
    const bytes = cte === 'base64' ? base64ToBytes(part.body)
      : cte === 'quoted-printable' ? quotedPrintableToBytes(part.body)
      : Uint8Array.from(part.body, c => c.charCodeAt(0) & 0xff);
    if (bytes.length > 0) out.attachmentsData.push({ fileName, mimeType: type, bytes });
    return out;
  }
  if (type.startsWith('image/') || type.startsWith('audio/') || type.startsWith('video/')) {
    return out; // inline media never contributes text
  }
  if (type.startsWith('text/')) {
    const decoded = decodePartBody(part, contentType.params['charset']);
    out.texts.push({ kind: type === 'text/html' ? 'html' : 'plain', text: decoded });
    return out;
  }
  if (type === 'message/rfc822') {
    const nested = collect(splitHeadersAndBody(part.body), depth + 1);
    out.texts.push(...nested.texts);
    out.attachments.push(...nested.attachments);
    out.attachmentsData.push(...nested.attachmentsData);
  }
  return out;
}

/* ── HTML → text ──────────────────────────────────────────────────── */

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
  ndash: '–', mdash: '—', hellip: '…', copy: '©', reg: '®',
  lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”', bull: '•',
};

function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (whole, ent: string) => {
    if (ent.startsWith('#x') || ent.startsWith('#X')) {
      const code = parseInt(ent.slice(2), 16);
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole;
    }
    if (ent.startsWith('#')) {
      const code = parseInt(ent.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole;
    }
    return NAMED_ENTITIES[ent] ?? whole;
  });
}

/** Strip an HTML email body to readable text (block boundaries become newlines). */
export function htmlToText(html: string): string {
  let s = html;
  s = s.replace(/<!--[\s\S]*?-->/g, ' ');
  s = s.replace(/<(script|style|head|title)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ');
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<\/(p|div|tr|li|h[1-6]|blockquote|table|pre|section|article|header|footer)\s*>/gi, '\n');
  s = s.replace(/<(p|div|tr|li|h[1-6]|blockquote|table|pre|section|article|header|footer)\b[^>]*>/gi, '\n');
  s = s.replace(/<\/(td|th)\s*>/gi, ' ');
  s = s.replace(/<[^>]+>/g, ' ');
  s = decodeEntities(s);
  s = s.replace(/ /g, ' ');
  s = s.replace(/[ \t]+/g, ' ');
  s = s.replace(/ *\n */g, '\n');
  s = s.replace(/\n{3,}/g, '\n\n');
  return s.trim();
}

/* ── Public API ───────────────────────────────────────────────────── */

function normaliseBody(texts: Collected['texts']): string {
  const segments = texts
    .map((t) => (t.kind === 'html' ? htmlToText(t.text) : t.text.replace(/\r\n/g, '\n').trim()))
    .filter((t) => t.length > 0);
  return segments.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Canonical text layout shared by the `.eml` and `.msg` readers: identity
 * header lines, a blank line, the body (or the no-body placeholder), and an
 * optional `Attachments:` trailer. `looksLikeParsedEmail` recognises exactly
 * this shape.
 */
export function composeEmailText(
  headers: ParsedEmail['headers'],
  body: string,
  attachments: readonly string[],
): string {
  const headerLines = IDENTITY_HEADERS
    .filter((key) => headers[key] !== undefined)
    .map((key) => `${IDENTITY_LABEL[key]}: ${headers[key]}`);
  const sections = [
    headerLines.join('\n'),
    body.length > 0 ? body : NO_READABLE_BODY,
    attachments.length > 0 ? `Attachments: ${attachments.join('; ')}` : '',
  ].filter((s) => s.length > 0);
  return sections.join('\n\n');
}

/** Parse raw `.eml` source into headers, body, attachments and canonical text. */
export function parseEmlToText(raw: string): ParsedEmail {
  const source = raw.replace(/^﻿/, '');
  const root = splitHeadersAndBody(source);
  const headers: ParsedEmail['headers'] = {};
  for (const key of IDENTITY_HEADERS) {
    const value = root.headers.get(key);
    if (value !== undefined && value.trim().length > 0) {
      headers[key] = decodeEncodedWords(value).replace(/\s+/g, ' ').trim();
    }
  }
  const isEmail = headers.subject !== undefined
    || headers.from !== undefined
    || (headers.to !== undefined && headers.date !== undefined)
    || root.headers.has('mime-version');

  const collected = collect(root, 0);
  const body = normaliseBody(collected.texts);
  const attachments = Array.from(new Set(collected.attachments.map((a) => a.trim()).filter(Boolean)));

  return { headers, body, attachments, attachmentsData: collected.attachmentsData, isEmail, text: composeEmailText(headers, body, attachments) };
}

/**
 * True when `text` is the output of `parseEmlToText` AND carries a readable
 * body. Used by the Documents tab noise filter: raw-MIME legacy uploads
 * (`MIME-Version:`, `Content-Type:` header lines, `--boundary` markers) and
 * header-only messages are still hidden; parsed emails with a body show.
 */
export function looksLikeParsedEmail(text: string | undefined | null): boolean {
  if (!text) return false;
  const normalised = text.replace(/\r\n/g, '\n').trim();
  const blank = normalised.indexOf('\n\n');
  if (blank < 0) return false;
  const headerLines = normalised.slice(0, blank).split('\n');
  if (headerLines.length === 0) return false;
  if (!headerLines.every((line) => /^(From|To|Cc|Date|Subject): /.test(line))) return false;
  let rest = normalised.slice(blank + 2).trim();
  rest = rest.replace(/\n\nAttachments: [^\n]*$/, '').replace(/^Attachments: [^\n]*$/, '').trim();
  if (rest.length === 0) return false;
  if (rest.startsWith(NO_READABLE_BODY)) return false;
  return true;
}

/** OLE2 compound-file signature: what a binary Outlook `.msg` starts with. */
export function isOleCompoundFile(bytes: Uint8Array): boolean {
  const sig = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
  if (bytes.length < sig.length) return false;
  return sig.every((b, i) => bytes[i] === b);
}
